import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSecurity } from '../../context/SecurityContext';
import { ADMIN_MENU_ITEMS, isAdminNavActive } from './adminMenuConfig';
import { filterAdminMenuByRole, getAdminRoleMeta, normalizeAdminRoleCode } from '../../utils/adminPermissions';
import AdminNotifications from './AdminNotifications';

const AVATAR_COLORS = [
  ['#2262e2', '#1b56c9'], ['#4a88ef', '#2262e2'], ['#16a34a', '#15803d'],
];

/** Nombre visible en la barra: full_name del API o respaldo legible. */
function getAdminTopbarLabel(adminUser) {
  const fullName = (adminUser?.full_name || adminUser?.displayName || '').trim();
  const email = (adminUser?.email || adminUser?.username || '').trim();
  if (fullName) return fullName;
  if (email) {
    const local = email.includes('@') ? email.split('@')[0] : email;
    return local ? local.charAt(0).toUpperCase() + local.slice(1) : 'Cuenta admin';
  }
  return 'Cuenta admin';
}

function getAdminAvatarInitial(adminUser) {
  const fullName = (adminUser?.full_name || adminUser?.displayName || '').trim();
  if (fullName) {
    const first = fullName.split(/\s+/).find(Boolean);
    return (first || fullName).charAt(0).toUpperCase();
  }
  const label = getAdminTopbarLabel(adminUser);
  return label.charAt(0).toUpperCase();
}

const AdminHeader = ({ onMenuToggle }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { adminUser, handleLogout } = useSecurity();

  const topbarLabel = getAdminTopbarLabel(adminUser);
  const roleMeta = getAdminRoleMeta(normalizeAdminRoleCode(adminUser?.role));
  const menuItems = useMemo(
    () => filterAdminMenuByRole(ADMIN_MENU_ITEMS, adminUser?.role),
    [adminUser?.role]
  );
  const userInitial = getAdminAvatarInitial(adminUser);
  const [ac1, ac2] = AVATAR_COLORS[Math.abs(userInitial.charCodeAt(0)) % AVATAR_COLORS.length];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogoutClick = () => {
    setShowUserMenu(false);
    handleLogout();
  };

  return (
    <header className="adm-topbar">
      <button
        type="button"
        className="adm-topbar-icon d-md-none"
        onClick={onMenuToggle}
        aria-label="Abrir menú"
      >
        <i className="bi bi-list" />
      </button>

      <nav className="adm-topnav" aria-label="Secciones del panel">
        {menuItems.map((item) => {
          const active = isAdminNavActive(location.pathname, item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`adm-topnav-pill ${active ? 'adm-topnav-pill--active' : ''}`}
            >
              {item.shortLabel || item.label}
            </Link>
          );
        })}
      </nav>

      <div className="adm-topbar-actions">
        <AdminNotifications />

        <div className="adm-topbar-user-wrap" ref={userMenuRef}>
          <button
            type="button"
            className="adm-topbar-user"
            onClick={() => setShowUserMenu(!showUserMenu)}
            aria-expanded={showUserMenu}
          >
            <span
              className="adm-topbar-avatar"
              style={{ background: `linear-gradient(135deg, ${ac1} 0%, ${ac2} 100%)` }}
            >
              {userInitial}
            </span>
            <span className="adm-topbar-user-name d-none d-sm-block" title={adminUser?.email || topbarLabel}>
              {topbarLabel}
            </span>
            <i className={`bi bi-chevron-down adm-topbar-user-chevron d-none d-sm-inline ${showUserMenu ? 'is-open' : ''}`} />
          </button>

          {showUserMenu && (
            <div className="adm-topbar-user-dropdown">
              <div className="adm-topbar-user-dropdown-header">
                <strong>{topbarLabel}</strong>
                {adminUser?.email && (
                  <span className="adm-topbar-user-dropdown-email">{adminUser.email}</span>
                )}
                {adminUser?.role && (
                  <span className={`badge bg-${roleMeta.color} mt-2`} style={{ fontSize: '0.7rem' }}>
                    {roleMeta.label}
                  </span>
                )}
              </div>
              <div className="adm-topbar-user-dropdown-divider" />
              <button type="button" className="adm-topbar-user-dropdown-item" onClick={() => { setShowUserMenu(false); navigate('/catalogo'); }}>
                <i className="bi bi-shop" />
                Ver tienda
              </button>
              <button type="button" className="adm-topbar-user-dropdown-item adm-topbar-user-dropdown-item--logout" onClick={handleLogoutClick}>
                <i className="bi bi-box-arrow-right" />
                Cerrar sesión
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default AdminHeader;
